import { useEffect, useState, type RefObject } from 'react'

/** Options for {@link useInView}. */
export interface UseInViewOptions {
  /** IntersectionObserver `rootMargin`, e.g. `'100% 0px'` to fire a viewport early. Default `'0px'`. */
  rootMargin?: string
  /** Visible fraction (0..1) that counts as "in view". Default `0`. */
  threshold?: number
  /** Stop observing after the first time it enters (lazy mounting). Default `false`. */
  once?: boolean
}

/**
 * Whether the element in `ref` intersects the viewport (expanded by `rootMargin`).
 * The element must be rendered by the same component that calls the hook.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null)
 * const seen = useInView(ref, { rootMargin: '0px 0px -20% 0px', once: true })
 */
export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin = '0px', threshold = 0, once = false }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        setInView(entry.isIntersecting)
        if (entry.isIntersecting && once) observer.disconnect()
      },
      { rootMargin, threshold },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, rootMargin, threshold, once])

  return inView
}
